import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DomestikaNextLiveCard } from "@domestika-dev/prudencio/dist/molecules/DomestikaNextLiveCard";
import { Grid, GridItem } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Text } from "@domestika-dev/prudencio/dist/atoms/Typography";
import { EmptySectionPlaceholder } from "./EmptySectionPlaceholder";
import When from "../../../../common/When";
import { LiveSessionContext } from "../../context/LiveSessionContext";
import { useLanguagesList } from "../../customHooks/useLanguagesList";
import { diffBetweenDates, millisToHours, subtractHours } from "../../utils/dateUtils";
import {
  canCancelLiveSession,
  canEditLiveSession,
  shouldShowReadyToLiveAlert,
} from "../../utils/liveSessionStates";
import { config } from "../../config";
import { getAssetPath } from "../../utils/assetsUtils";

const loadingNextLiveProps = { loading: true };

export const NextLive = () => {
  const {
    nextLiveLoading,
    hasActiveSession,
    course,
    liveSession,
    user,
    isTeacher,
    onEnterLive,
    onScheduleCancel,
    onScheduleEdit,
  } = useContext(LiveSessionContext);

  const intl = useIntl();
  const [isReadyToLive, setReadyToLive] = useState(false);
  const [nextLiveSessionProps, setNextLiveSessionProps] = useState(loadingNextLiveProps);

  const { getLanguagesList } = useLanguagesList();
  const languagesList = useRef(getLanguagesList());
  const getLanguageLabel = useCallback(
    (language) => languagesList.current?.find(({ value }) => value === language)?.label,
    [languagesList]
  );

  useEffect(() => {
    let nextLiveProps = nextLiveSessionProps;

    if (!course?.id || nextLiveLoading || !user?.id) {
      nextLiveProps = loadingNextLiveProps;
    } else if (hasActiveSession) {
      const dropdownMenuOptions = [];
      if (isTeacher) {
        if (canEditLiveSession(liveSession.aasmState)) {
          dropdownMenuOptions.push({
            label: intl.formatMessage({ id: "live_sessions.teacher_next_live.edit" }),
            onClick: onScheduleEdit,
          });
        }

        if (canCancelLiveSession(liveSession.aasmState)) {
          dropdownMenuOptions.push({
            label: intl.formatMessage({ id: "live_sessions.teacher_next_live.delete" }),
            onClick: onScheduleCancel,
          });
        }
      }

      nextLiveProps = {
        streamers: [
          {
            name: course?.teacher.name,
            avatar: getAssetPath(course?.teacher.avatar?.url),
          },
        ],
        liveInfo: {
          scheduledAt: liveSession.formattedScheduledAt,
          language: getLanguageLabel(liveSession.language),
          title: liveSession.title,
          coverUrl: getAssetPath(course?.openGraphCover),
        },
        showDropdownMenu: isTeacher && !!dropdownMenuOptions.length,
        dropdownMenuOptions,
        onClick: onEnterLive,
        isNotYetReadyToLive: isTeacher && !isReadyToLive,
        isReadyToLive: isTeacher && isReadyToLive,
        readyToLiveButtonText: intl.formatMessage({
          id: "live_sessions.teacher_next_live.button",
        }),
        footerMessage: intl.formatMessage({
          id: "live_sessions.teacher_next_live.ready",
        }),
      };
    } else if (nextLiveSessionProps) {
      nextLiveProps = null;
    }

    setNextLiveSessionProps(nextLiveProps);
  }, [course, nextLiveLoading, user, isReadyToLive]);

  useEffect(() => {
    let readyToLiveTimeout;

    if (nextLiveSessionProps && !nextLiveSessionProps.loading) {
      if (
        isTeacher &&
        liveSession.scheduledAt &&
        shouldShowReadyToLiveAlert(liveSession.aasmState)
      ) {
        const sessionReadyDate = subtractHours(
          new Date(liveSession.scheduledAt),
          config.READY_TO_LIVE_ALERT_HOURS_NOTICE
        );
        const timeRemaining = diffBetweenDates(new Date(), sessionReadyDate);
        const hoursRemaining = millisToHours(timeRemaining);
        if (hoursRemaining <= 0) {
          setReadyToLive(true);
        } else {
          readyToLiveTimeout = setTimeout(() => {
            setReadyToLive(true);
          }, timeRemaining);
        }
      }
    }

    return () => {
      if (readyToLiveTimeout) {
        clearTimeout(readyToLiveTimeout);
      }
    };
  }, [nextLiveSessionProps]);

  return (
    <Grid gridRowGap={[5, 5, 5]} marginBottom={8}>
      <GridItem gridRow={1} gridColumn="1 / span 8">
        <Text size="medium" fontWeight="medium" as="h2" textAlign="left">
          <FormattedMessage
            id={
              isTeacher ? "live_sessions.teacher_next_live.title" : "live_sessions.next_live.title"
            }
          />
        </Text>
      </GridItem>
      <GridItem gridRow={2} gridColumn="1 / span 8">
        <When condition={!!nextLiveSessionProps}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {() => <DomestikaNextLiveCard {...nextLiveSessionProps} />}
        </When>
        <When condition={!nextLiveSessionProps && !isTeacher}>
          {() => (
            <EmptySectionPlaceholder
              message={intl.formatMessage({ id: "live_sessions.next_live.body" })}
            />
          )}
        </When>
      </GridItem>
    </Grid>
  );
};

export default NextLive;
